import { Amendable } from 'store/declarations/declaration';
import { ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { ICS2Notification } from 'store/declarations/eu/ics2/ics2-notification';
import { withoutEmptyElements } from 'store/declarations/eu/ics2/without-empty-elements';
import { removeEmptyObjects } from 'views/declarations/utils/form-utils';

/** Message types offered by the ENS (ICS2) creation page. */
export const ics2EnsNameLabels = [
    {
        key: ICS2MessageTypes.F11,
        value: 'F11',
    },
    {
        key: ICS2MessageTypes.F50,
        value: 'F50',
    },
];

export enum ICS2GoodsItemCategory {
    MASTER = 'MASTER',
    HOUSE = 'HOUSE',
}

export interface ICS2Address {
    city?: string | null;
    country?: string | null;
    subDivision?: string | null;
    street?: string | null;
    postCode?: string | null;
    streetAdditionalLine?: string | null;
    number?: string | null;
    poBox?: string | null;
}

export interface ICS2PlaceAddress {
    country?: string | null;
}

export interface ICS2Communication {
    identifier?: string | null;
    type?: string | null;
}

/** Consignor, consignee, carrier, declarant, buyer and seller all share this shape. */
export interface ICS2Party {
    name?: string | null;
    identificationNumber?: string | null;
    typeOfPerson?: string | null;
    address?: ICS2Address;
    communication?: ICS2Communication[];
    customerId?: string | null;
}

/** The notify party carries a single communication rather than a list. */
export interface ICS2NotifyParty extends Omit<ICS2Party, 'communication'> {
    communication?: ICS2Communication;
}

export interface ICS2Representative {
    name?: string | null;
    identificationNumber?: string | null;
    status?: string | null;
    address?: ICS2Address;
    communication?: ICS2Communication[];
    customerId?: string | null;
}

export interface ICS2Place {
    location?: string;
    unlocode?: string;
    address?: ICS2PlaceAddress;
}

export interface ICS2DateAndTime {
    /** `yyyy-MM-dd'T'HH:mm:ss'Z'` */
    dateTime?: string;
}

export interface ICS2CountryOfRouting {
    sequenceNumber?: number;
    country?: string;
}

export interface ICS2Seal {
    identifier?: string;
}

export interface ICS2TransportEquipment {
    containerIdentificationNumber?: string;
    containerSizeAndType?: string;
    containerPackedStatus?: string;
    containerSupplierType?: string;
    numberOfSeals?: number;
    seal?: ICS2Seal[];
}

export interface ICS2SupportingDocument {
    referenceNumber?: string;
    type?: string;
}

export interface ICS2AdditionalInformation {
    code?: string;
    text?: string;
}

export interface ICS2AdditionalSupplyChainActor {
    identificationNumber?: string;
    role?: string;
}

export interface ICS2TransportDocument {
    documentNumber?: string;
    type?: string;
}

export interface ICS2ReferenceNumberUCR {
    referenceNumberUCR?: string;
}

export interface ICS2TransportCharges {
    methodOfPayment?: string;
}

export interface ICS2PassiveBorderTransportMeans {
    identificationNumber?: string;
    typeOfIdentification?: string;
    typeOfMeansOfTransport?: string;
    nationality?: string;
}

export interface ICS2ActiveBorderTransportMeans {
    identificationNumber?: string;
    typeOfIdentification?: string;
    typeOfMeansOfTransport?: string;
    nationality?: string;
    modeOfTransport?: string;
    actualDateAndTimeOfDeparture?: ICS2DateAndTime;
    estimatedDateAndTimeOfDeparture?: ICS2DateAndTime;
    estimatedDateAndTimeOfArrival?: ICS2DateAndTime;
    countriesOfRoutingOfMeansOfTransport?: ICS2CountryOfRouting[];
}

export interface ICS2SplitConsignment {
    splitConsignmentIndicator?: string;
    previousMRN?: string;
}

export interface ICS2CustomsOfficeOfFirstEntry {
    referenceNumber?: string;
}

export interface ICS2GoodsShipment {
    buyer?: ICS2Party;
    seller?: ICS2Party;
}

export interface ICS2CommodityCode {
    harmonizedSystemSubHeadingCode?: string;
    combinedNomenclatureCode?: string;
}

export interface ICS2DangerousGoods {
    unNumber?: string;
}

export interface ICS2Commodity {
    descriptionOfGoods?: string;
    cusCode?: string;
    commodityCode?: ICS2CommodityCode;
    dangerousGoods?: ICS2DangerousGoods[];
}

export interface ICS2Packaging {
    shippingMarks?: string;
    numberOfPackages?: number;
    typeOfPackages?: string;
}

export interface ICS2Weight {
    grossMass?: number | string | null;
}

/**
 * A goods item, on any level of either message type. F11 master items (`GoodsItemType64`) and house
 * items (`GoodsItemType80`) have the same shape, and F50 house items (`GoodsItemType15`) add the
 * transport means the item moved on.
 */
export interface ICS2GoodsItem {
    /** Assigned by the backend; absent while the item is being created. */
    id?: string;
    goodsItemNumber?: number;
    commodity?: ICS2Commodity;
    weight?: ICS2Weight;
    packaging?: ICS2Packaging[];
    transportEquipment?: ICS2TransportEquipment[];
    supportingDocuments?: ICS2SupportingDocument[];
    additionalInformation?: ICS2AdditionalInformation[];
    additionalSupplyChainActor?: ICS2AdditionalSupplyChainActor[];
    /** F50 only — F11 carries the passive border transport means on the house consignment instead. */
    passiveBorderTransportMeans?: ICS2PassiveBorderTransportMeans[];
}

/** Shape returned by the products endpoints: the goods item unwrapped, plus its metadata. */
export interface ICS2GoodsItemResponse extends ICS2GoodsItem {
    category?: ICS2GoodsItemCategory;
    houseConsignmentId?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface ICS2ConsignmentHouseLevel {
    /**
     * Assigned by the backend. Leaving it empty on save creates a new house consignment; keeping it
     * updates the existing one; omitting the whole entry from the master level deletes it.
     */
    id?: string;
    containerIndicator?: string;
    totalGrossMass?: number;
    referenceNumberUCR?: ICS2ReferenceNumberUCR;
    consignor?: ICS2Party;
    consignee?: ICS2Party;
    notifyParty?: ICS2NotifyParty;
    placeOfAcceptance?: ICS2Place;
    placeOfDelivery?: ICS2Place;
    transportCharges?: ICS2TransportCharges;
    transportDocumentHouseLevel?: ICS2TransportDocument;
    transportEquipment?: ICS2TransportEquipment[];
    passiveBorderTransportMeans?: ICS2PassiveBorderTransportMeans[];
    countriesOfRoutingOfConsignment?: ICS2CountryOfRouting[];
    supportingDocuments?: ICS2SupportingDocument[];
    additionalInformation?: ICS2AdditionalInformation[];
    additionalSupplyChainActor?: ICS2AdditionalSupplyChainActor[];
    goodsShipment?: ICS2GoodsShipment;
    /** Managed through the products endpoints, not by editing this array directly. */
    goodsItem?: ICS2GoodsItem[];
}

export interface ICS2ConsignmentMasterLevel {
    containerIndicator?: string;
    totalGrossMass?: number;
    referenceNumberUCR?: ICS2ReferenceNumberUCR;
    consignor?: ICS2Party;
    consignee?: ICS2Party;
    notifyParty?: ICS2NotifyParty;
    carrier?: ICS2Party;
    placeOfAcceptance?: ICS2Place;
    placeOfLoading?: ICS2Place;
    placeOfUnloading?: ICS2Place;
    placeOfDelivery?: ICS2Place;
    transportCharges?: ICS2TransportCharges;
    transportDocumentMasterLevel?: ICS2TransportDocument;
    transportEquipment?: ICS2TransportEquipment[];
    supportingDocuments?: ICS2SupportingDocument[];
    additionalInformation?: ICS2AdditionalInformation[];
    additionalSupplyChainActor?: ICS2AdditionalSupplyChainActor[];

    goodsItem?: ICS2GoodsItem[];
    /**
     * House consignments. The backend treats this array as the source of truth on every declaration
     * update, so it must always be sent back in full.
     */
    consignmentHouseLevel?: ICS2ConsignmentHouseLevel[];
}

export interface ICS2EnsDeclarationPayload {
    lrn?: string;
    documentIssueDate?: ICS2DateAndTime;
    specificCircumstanceIndicator?: string;
    reEntryIndicator?: string;
    splitConsignment?: ICS2SplitConsignment;
    representative?: ICS2Representative;
    activeBorderTransportMeans?: ICS2ActiveBorderTransportMeans;
    declarant?: ICS2Party;
    customsOfficeOfFirstEntry?: ICS2CustomsOfficeOfFirstEntry;
    consignmentMasterLevel?: ICS2ConsignmentMasterLevel;
}

/**
 * What the declaration endpoints return under `ics2ENSDeclaration`. The payload of the message sits
 * under `payload`, beside the metadata the backend owns - the same envelope the create and update
 * calls take.
 */
export interface ICS2EnsDeclaration extends Amendable {
    id?: string;
    status?: string;
    messageType?: ICS2MessageTypes;
    sourceManifestId?: string | null;
    payload?: ICS2EnsDeclarationPayload;
    numberOfHouse?: number;
    numberOfHouseItems?: number;
    numberOfMasterItems?: number;
    notifications?: ICS2Notification[];
    sourceManifestJobId?: string | null;
    jobReferenceId?: string | null;
    houseConsignments?: ICS2EnsHouseConsignment[];
}

export interface ICS2EnsHouseConsignment {
    id?: string;
    sourceJobId?: string | null;
    jobReferenceId?: string | null;
}

/** Body of the create/update declaration calls. */
export interface ICS2EnsDeclarationRequest {
    messageType: ICS2MessageTypes;
    payload: ICS2EnsDeclarationPayload;
}

/**
 * Turns what a GET returned under `ics2ENSDeclaration` back into the `{ messageType, payload }` body
 * the create/update endpoints expect. Everything the backend owns — ids, counters, notifications,
 * amendment flags — is dropped, because the payload is deserialized into the request type of the
 * message.
 *
 * `consignmentMasterLevel.goodsItem` and `consignmentMasterLevel.consignmentHouseLevel` are
 * deliberately kept: the backend treats both arrays as the source of truth on every update and
 * deletes anything missing from them.
 *
 * Empty objects come off too, as they do on every form save. The house consignment saves send the
 * stored declaration without going through the form, and an object sent empty is serialized as an
 * empty element, which customs rejects - a blank reference number/UCR was rejected that way.
 *
 * Objects left holding only empty lists come off as well (`withoutEmptyElements`): the cleanup above
 * keeps empty lists, so a representative with no communication would still be sent as an empty element.
 */
export const toIcs2EnsDeclarationRequest = (declaration: ICS2EnsDeclaration = {}): ICS2EnsDeclarationRequest => ({
    messageType: declaration.messageType as ICS2MessageTypes,
    payload: withoutEmptyElements(removeEmptyObjects(withoutClientOnlyFields(declaration.payload ?? {}))),
});

/**
 * Fields the forms keep beside the message but the message itself does not carry. The backend
 * deserializes the payload into the request type of the message and rejects anything it does not
 * know, so a field the app added for its own use has to come off before the payload is sent.
 *
 * `customerId` is the only one: `PartiesCard` writes it onto a party to remember which customer the
 * details were filled from. It sits on ten different parties across the tree - the declarant, the
 * representative, four parties of the master level, three of a house consignment and the buyer and
 * seller of its goods shipment - so it is taken off by name wherever it appears rather than at each
 * of those paths, which is both shorter and leaves no gap when a party is added.
 */
const CLIENT_ONLY_FIELDS = ['customerId'];

export const withoutClientOnlyFields = <T>(value: T): T => {
    if (Array.isArray(value)) return value.map(withoutClientOnlyFields) as unknown as T;

    // Only plain objects are rebuilt: anything else carrying a prototype - a date, say - would lose
    // it, and nothing the payload holds needs walking into.
    if (value === null || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) {
        return value;
    }

    return Object.fromEntries(
        Object.entries(value)
            .filter(([key]) => !CLIENT_ONLY_FIELDS.includes(key))
            .map(([key, entry]) => [key, withoutClientOnlyFields(entry)])
    ) as T;
};

/**
 * Strips the item metadata the products endpoints add on top of the goods item, so that only the
 * goods item itself is sent back. `id` has to go as well: the item is addressed by the URL, while
 * the `id` inside the goods item type means something else entirely to the backend.
 *
 * `category` and `houseConsignmentId` are left in place — the products layer reads them to pick the
 * level the item is saved on and removes them before the request goes out.
 */
export const toIcs2EnsGoodsItemRequest = (
    goodsItem: ICS2GoodsItemResponse = {}
): ICS2GoodsItem & Pick<ICS2GoodsItemResponse, 'category' | 'houseConsignmentId'> => {
    const { id, createdAt, updatedAt, ...item } = goodsItem;

    return item;
};

/**
 * A listed goods item as the declaration payload carries it, which is the mirror image of the strip
 * above: the products endpoints return the item wrapped in metadata, and the goods item type of the
 * payload holds none of it.
 *
 * `id` is the exception, and stays. It is metadata to the products endpoints, which address an item
 * by its url - but the declaration update matches an item on it, creating the entries that carry
 * none and updating the ones that do, so dropping it would recreate every item on every save.
 */
export const toIcs2EnsPayloadGoodsItem = ({
    createdAt,
    updatedAt,
    category,
    houseConsignmentId,
    ...goodsItem
}: ICS2GoodsItemResponse = {}): ICS2GoodsItem => goodsItem;
